import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-picker';
import { TimeGranularitySelect, type TimeGranularity } from '@/components/ui/time-granularity-select';
import { SimpleBarChart, DataTable, type ChartDataPoint } from '@/components/ui/simple-chart';
import { client, formatPrice } from '@vn-propane/shared';

interface DriverDeliveries {
  driver_id: string;
  driver_name: string | null;
  delivery_count: number;
  total_revenue: number;
}

interface ReportSummary {
  total: number;
  count: number;
  average: number;
  min: number;
  max: number;
}

interface TimeSeriesReport {
  report_type: string;
  granularity: string;
  start_date: string;
  end_date: string;
  data: ChartDataPoint[];
  summary: ReportSummary;
}

interface DeliveriesReport {
  time_series: TimeSeriesReport;
  by_driver: DriverDeliveries[];
}

function getDefaultDateRange(): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

export default function DeliveriesReportPage() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation('common');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DeliveriesReport | null>(null);

  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState<Date | undefined>(defaultRange.start);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultRange.end);
  const [granularity, setGranularity] = useState<TimeGranularity>('day');

  const fetchData = useCallback(async () => {
    if (!orgId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await client.get({
        url: `/gas_admin/organizations/${orgId}/reports/deliveries`,
        query: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          granularity,
        },
      });

      if (response.error) {
        setError('Failed to load deliveries report');
        return;
      }

      setData((response.data as { data: DeliveriesReport } | undefined)?.data ?? null);
    } catch (err) {
      setError('Failed to load deliveries report');
    } finally {
      setLoading(false);
    }
  }, [orgId, startDate, endDate, granularity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.filters')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startLabel={t('labels.from')}
            endLabel={t('labels.to')}
          />
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">{t('reports.granularityLabel')}:</span>
            <TimeGranularitySelect
              value={granularity}
              onChange={setGranularity}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-8 text-muted-foreground">
          {t('labels.loading')}
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Summary */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.totalDeliveries')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{data.time_series.summary.count}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.averagePerPeriod')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{Number(data.time_series.summary.average).toFixed(1)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.minPeriod')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{data.time_series.summary.min}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('reports.metrics.maxPeriod')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tracking-tight">{data.time_series.summary.max}</div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle>{t('reports.deliveriesOverTime')}</CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart
                data={data.time_series.data}
                height={250}
                formatValue={(v) => v.toString()}
              />
            </CardContent>
          </Card>

          {/* By Driver and Data Table */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('reports.deliveriesByDriver')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.by_driver.length === 0 ? (
                  <p className="text-muted-foreground">{t('reports.noData')}</p>
                ) : (
                  <div className="space-y-3">
                    {data.by_driver.map((driver) => (
                      <div key={driver.driver_id} className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">
                            {driver.driver_name || t('reports.unknownDriver')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatPrice(String(driver.total_revenue), 'VND')} {t('reports.revenue')}
                          </div>
                        </div>
                        <div className="font-medium">
                          {driver.delivery_count} {t('reports.deliveries')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('reports.dataTable')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={data.time_series.data}
                  valueLabel={t('reports.metrics.deliveries')}
                  dateLabel={t('reports.tableDate')}
                  countLabel={t('reports.tableCount')}
                  formatValue={(v) => v.toString()}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
